import type {
  BlocksUser,
  InviteUserPayload,
  UsersResponse,
} from "@/src/models/user.model";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { BLOCKS_USER_QUERY_KEYS } from "../query-keys/users.keys";
import {
  COMMENTS_QUERY_KEY,
  POSTS_QUERY_KEY,
  REACTIONS_QUERY_KEY,
} from "../query-keys/post.keys";
import { NEWS_QUERY_KEY } from "../query-keys/news.keys";
import {
  WIKI_ARTICLE_QUERY_KEY,
  WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY,
  WIKI_DRAFT_ARTICLES_QUERY_KEY,
  WIKI_SAVED_ARTICLES_QUERY_KEY,
} from "../query-keys/wiki.keys";
import {
  EVENTS_QUERY_KEY,
  EVENT_GALLERY_QUERY_KEY,
  RSVPS_QUERY_KEY,
} from "../query-keys/events.keys";
import { decodeToken } from "./api/auth.api";
import apiClient from "./api/axios";
import {
  fetchMe,
  fetchUser,
  updateMe,
  updateUserById,
  type BlocksMe,
} from "@/src/services/blocks/session";
import { BLOCKS_ENDPOINTS } from "@/src/services/blocks/config";
import {
  appRoles,
  isRealOrgId,
  mergePlatformRoles,
  withPlatformRoles,
} from "@/src/constants/roles-permissions.constants";
import { purgeUserData } from "./user-data-purge";
import { syncUserNameSnapshots } from "./user-name-snapshot-sync";
import gqlClient from "./api/graphql.client";
import { INSERT_USER_PROFILE } from "./profile/profile.query";
import { generateStrongPassword } from "@/src/utils/form-validators";

const PAGE_SIZE = 10;
const ALL_PAGE_SIZE = 1000000;
const BATCH_SIZE = 20;

/**
 * The list route returns `roles` as an **organization-keyed object** —
 * `{"<orgId>": ["admin","clouduser"]}` — and carries **no** `permissions`,
 * `memberships`, `phoneNumber` or `language`. `/iam/me` and
 * `GET /iam/users/{id}` return `roles` as a flat array instead. Verified live
 * 2026-07-30 once the routes became readable.
 *
 * Consumers read `user.memberships[…].roles` (e.g. `screens/uam/uam.tsx` splits
 * admins from members on `memberships[0].roles[0]`), so the org map is projected
 * into that shape here. Without this every list-driven role check silently sees
 * `undefined` and the UAM tabs come back empty.
 *
 * `permissions` is `[]` rather than invented: the list genuinely does not carry
 * it, and a direct-vs-role distinction matters (see `usePermissions`).
 */
const toBlocksUserList = (raw: readonly any[]): BlocksUser[] =>
  raw.map((user) => {
    const roleMap: Record<string, readonly string[]> =
      user?.roles && !Array.isArray(user.roles) ? user.roles : {};
    const memberships = Array.isArray(user?.memberships)
      ? user.memberships
      : Object.entries(roleMap).map(([organizationId, roles]) => ({
        organizationId,
        // `clouduser` is platform access, not an app role — see appRoles.
        roles: appRoles(roles),
        permissions: [],
      }));

    // The list spells it `loginCount`; `/iam/me` and the by-id read spell it
    // `logInCount`, which is what `BlocksUser` exposes.
    return {
      ...user,
      logInCount: user?.logInCount ?? user?.loginCount ?? 0,
      memberships,
    } as BlocksUser;
  });

/**
 * v4 list/search — `POST /iam/v4/iam/users` (replaces v1 `Iam/GetUsers`).
 * Readable since the project granted IAM read rights (2026-07-30); the response
 * envelope `{ totalCount, data: [user] }` is unchanged. `projectKey` is gone (the
 * `x-blocks-key` header carries the tenant).
 *
 * Filter behaviour, measured against the live project rather than assumed:
 * - `userIds` works (the leaderboard batch depends on it), as does
 *   `status: {active, inactive}`.
 * - `email` is an **exact match**, not a substring — searching a partial address
 *   returns nothing, which is why the callers below fall back from `name` to
 *   `email` on an empty result.
 * - `name` is applied (a non-matching value returns 0) but could not be
 *   characterised as exact-vs-substring: every user in this project currently has
 *   an empty `firstName`/`lastName`.
 * - `search` is **ignored** on this route (unlike `/iam/permissions`, where it is
 *   required and does match substrings).
 * - ⚠ **`org_id` is ignored too** — a deliberately bogus org id still returns
 *   every user. It is kept below so scoping starts working if Blocks honours it,
 *   but do NOT rely on this route for tenant isolation today.
 */
const fetchUsers = async (
  page: number,
  search: string,
  startDate?: string,
  searchBy: "name" | "email" = "name",
  pageSize: number = PAGE_SIZE,
) => {
  const decoded = await decodeToken();
  const currentOrgId = decoded?.org_id;

  return apiClient.post(BLOCKS_ENDPOINTS.users, {
    page,
    pageSize,
    sort: { property: "CreatedDate", isDescending: false },
    filter: {
      name: searchBy === "name" ? search : "",
      email: searchBy === "email" ? search : "",
      ...(startDate && { joinedOn: startDate }),
      ...(currentOrgId && { org_id: currentOrgId }),
    },
  });
};


export const useGetActiveUsers = () => {
  return useQuery({
    queryKey: ['activeUsers'],
    queryFn: async () => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      return apiClient.post(BLOCKS_ENDPOINTS.users, {
        filter: {
          name: "",
          email: "",
          status: {
            active: true,
            inactive: false
          },
          ...(currentOrgId && { org_id: currentOrgId }),
        },
      });
    },
  });
};

const filterByEndDate = (
  users: ReadonlyArray<BlocksUser>,
  endDate: string,
): ReadonlyArray<BlocksUser> => {
  const endDateTime = new Date(endDate);
  endDateTime.setHours(23, 59, 59, 999);
  return users.filter(
    (user) => new Date(user.createdDate).getTime() <= endDateTime.getTime(),
  );
};

export const fetchBlocksUsersByIds = async (
  userIds: ReadonlyArray<string>,
): Promise<Record<string, BlocksUser>> => {
  const uniqueUserIds = [...new Set(userIds)];
  const usersMap: Record<string, BlocksUser> = {};
  const decoded = await decodeToken();
  const currentOrgId = decoded?.org_id;

  for (let i = 0; i < uniqueUserIds.length; i += BATCH_SIZE) {
    const batch = uniqueUserIds.slice(i, i + BATCH_SIZE);
    try {
      const response = await apiClient.post(BLOCKS_ENDPOINTS.users, {
        page: 0,
        pageSize: BATCH_SIZE,
        filter: {
          name: "",
          email: "",
          // The list filters by `userIds` (matches every other call in the app);
          // `itemIds` is ignored by the API, so the batch came back without the
          // requested users and the leaderboard fell back to showing raw ids.
          userIds: batch,
          ...(currentOrgId && { org_id: currentOrgId }),
        },
      });
      const users: BlocksUser[] = toBlocksUserList(response.data?.data ?? []);
      users.forEach((blocksUser) => {
        usersMap[blocksUser.itemId] = blocksUser;
      });
    } catch { }
  }

  return usersMap;
};

let avatarBatchIds = new Set<string>();
let avatarBatchPromise: Promise<Record<string, BlocksUser>> | null = null;

const loadBlocksUser = (userId: string): Promise<BlocksUser | undefined> => {
  avatarBatchIds.add(userId);
  if (!avatarBatchPromise) {
    avatarBatchPromise = new Promise((resolve) => {
      setTimeout(async () => {
        const ids = [...avatarBatchIds];
        avatarBatchIds = new Set();
        avatarBatchPromise = null;
        resolve(await fetchBlocksUsersByIds(ids));
      }, 50);
    });
  }
  return avatarBatchPromise.then((map) => map[userId]);
};

export const useBlocksUser = (userId?: string) =>
  useQuery({
    queryKey: BLOCKS_USER_QUERY_KEYS.avatarById(userId ?? ""),
    queryFn: () => loadBlocksUser(userId as string),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

export const useUsersQuery = (
  page: number = 0,
  search: string = "",
  startDate?: string,
  endDate?: string,
) =>
  useQuery<UsersResponse>({
    queryKey: BLOCKS_USER_QUERY_KEYS.users(page, search, startDate, endDate),
    queryFn: async () => {
      const nameResponse = await fetchUsers(
        page,
        search,
        startDate,
        "name",
        PAGE_SIZE,
      );
      let users: ReadonlyArray<BlocksUser> = toBlocksUserList(
        nameResponse.data.data,
      );
      let totalCount: number = nameResponse.data.totalCount;

      if (users.length === 0 && search.trim() !== "") {
        const emailResponse = await fetchUsers(
          page,
          search,
          startDate,
          "email",
          PAGE_SIZE,
        );
        users = toBlocksUserList(emailResponse.data.data);
        totalCount = emailResponse.data.totalCount;
      }

      return {
        users: endDate ? filterByEndDate(users, endDate) : users,
        totalCount,
        pageSize: PAGE_SIZE,
      };
    },
    placeholderData: (previousData) => previousData,
  });

/**
 * All org member emails, for a broadcast send (news publish, etc.) that has no
 * per-recipient UI of its own. Plain async, not a hook — callers that need it
 * inside a mutation (rather than render) call this directly.
 *
 * Deliberately skips the empty-search email-fallback branch `useAllUsersQuery`
 * has: that branch only fires when `search` is non-empty, which is never true
 * here.
 */
export const fetchAllUserEmails = async (): Promise<string[]> => {
  const response = await fetchUsers(0, "", undefined, "name", ALL_PAGE_SIZE);
  const users = toBlocksUserList(response.data?.data ?? []);
  return users.map((user) => user.email).filter(Boolean);
};

export const useAllUsersQuery = (
  search: string = "",
  startDate?: string,
  endDate?: string,
  enabled: boolean = false,
) =>
  useQuery<UsersResponse>({
    queryKey: BLOCKS_USER_QUERY_KEYS.allUsers(search, startDate, endDate),
    queryFn: async () => {
      const nameResponse = await fetchUsers(
        0,
        search,
        startDate,
        "name",
        ALL_PAGE_SIZE,
      );
      let users: ReadonlyArray<BlocksUser> = toBlocksUserList(
        nameResponse.data.data,
      );
      let totalCount: number = nameResponse.data.totalCount;

      if (users.length === 0 && search.trim() !== "") {
        const emailResponse = await fetchUsers(
          0,
          search,
          startDate,
          "email",
          ALL_PAGE_SIZE,
        );
        users = toBlocksUserList(emailResponse.data.data);
        totalCount = emailResponse.data.totalCount;
      }

      return {
        users: endDate ? filterByEndDate(users, endDate) : users,
        totalCount,
        pageSize: ALL_PAGE_SIZE,
      };
    },
    enabled,
    placeholderData: (previousData) => previousData,
  });

/**
 * v4 create — `POST /iam/v4/iam/users/create` (per the blocks-iam-users skill
 * docs; a real route, reachable since the IAM grant but not probed by actually
 * creating a user). Replaces the v1 `Iam/Create`.
 *
 * The v4 body is flat: no `projectKey` (header carries the tenant), no
 * `memberships[]` — roles are a top-level `roles[]` (by slug) scoped to a single
 * `organizationId`. Invite-and-activate: create with an empty `password`, then
 * the user sets one via `/auth/activate`. `userPassType: 1` (Password) and
 * `userCreationType: 1` (Portal, admin-created) are the recommended defaults.
 */
/**
 * Creates one account and returns its new user id.
 *
 * The response is `{ itemId, errors, isSuccess }` — so, as elsewhere on this
 * platform, HTTP 200 does not mean it worked. `isSuccess: false` is checked here
 * rather than left for the caller to notice, because the id is what a profile is
 * later attached to: treating a failed create as successful would attach a
 * profile to `undefined`.
 */
const inviteUser = async (payload: InviteUserPayload): Promise<string> => {
  const decoded = await decodeToken();
  const currentOrgId = decoded?.org_id;

  const response = await apiClient.post(BLOCKS_ENDPOINTS.usersCreate, {
    email: payload.email,
    userName: payload.email,
    // Invited accounts only ever sign in via SSO — this is generated purely
    // to activate the account at creation time (an empty `password` leaves it
    // pending Blocks' own activation-email flow — see generateStrongPassword
    // in form-validators.ts). Never logged, stored, returned, or shown.
    password: generateStrongPassword(),
    firstName: payload.firstName,
    lastName: payload.lastName,
    userPassType: 1,
    userCreationType: 1,
    mfaEnabled: false,
    // Platform access is appended to every invite. Without `clouduser` the new
    // account cannot reach Blocks platform endpoints, and no later write adds
    // it: `mergePlatformRoles` on the update paths only preserves what the
    // record already carries. It stays invisible in the app — `appRoles` strips
    // it from every badge, picker and permission check.
    roles: withPlatformRoles([payload.role ?? "member"]),
    permissions: [],
    organizationId: currentOrgId,
    platform: "blocks_portal",
  });

  const { itemId, isSuccess, errors } = response.data ?? {};

  if (isSuccess === false || !itemId) {
    const detail =
      (errors && Object.values(errors).flat().join(", ")) ||
      "account was not created";
    throw new Error(`${payload.email}: ${detail}`);
  }

  return itemId as string;
};

/** How many accounts to create at once. See the note in useInviteUsers. */
const INVITE_CONCURRENCY = 5;

export type InviteUsersResult = {
  createdCount: number;
  failures: { email: string; reason: string }[];
};

export type InviteProgress = {
  completed: number;
  total: number;
  /** Who a just-started request belongs to. Absent on the "settled" tick. */
  current?: { firstName: string; lastName: string; email: string };
};

export type InviteUsersInput = {
  users: (InviteUserPayload & {
    country?: string | null;
    department?: string | null;
    designation?: string | null;
  })[];
  /**
   * Fired once when a request starts (with `current`) and once when it settles
   * (with an incremented `completed`, no `current`). Concurrency is
   * `INVITE_CONCURRENCY`, so up to that many "start" ticks can arrive before the
   * first "settled" one — the displayed name is a best-effort "one of the ones
   * in flight right now", not a strict queue position.
   */
  onProgress?: (progress: InviteProgress) => void;
};

/**
 * The reason to report for one failed invite.
 *
 * `errors.Email` comes off an axios error body and is often an ARRAY of strings
 * ("Email already exists"), which rendered as "[object Object]" / a comma soup in
 * the toast. Joined here so every consumer gets a sentence.
 */
const inviteFailureReason = (error: any): string => {
  const emailErrors = error?.response?.data?.errors?.Email;
  if (Array.isArray(emailErrors) && emailErrors.length) {
    return emailErrors.join(", ");
  }
  if (typeof emailErrors === "string" && emailErrors) return emailErrors;
  return error?.message || "unknown error";
};

/**
 * Creates accounts for a list of people, and seeds a profile for anyone who came
 * with a country.
 *
 * There is no bulk endpoint on the platform, so this is N separate account
 * creations. The whole point of the shape below is that they are INDEPENDENT —
 * one bad address must never cancel, skip or corrupt anyone else's invite:
 *
 * 1. **Bounded concurrency.** An Excel import can carry ~900 rows, and firing 900
 *    account creations simultaneously is the kind of thing that fails partway with
 *    no way to tell what landed.
 * 2. **Per-user outcomes.** Each person succeeds or fails on their own and the
 *    failures come back with reasons, so a re-run can target just those rows.
 * 3. **`Promise.allSettled`, not `Promise.all`.** Every task already catches its
 *    own errors, but `Promise.all` means any throw the inner catch does not cover
 *    rejects the batch — which `await` then throws out of the `for` loop, so
 *    every REMAINING batch is silently never sent. `allSettled` makes that
 *    impossible: the loop always runs to the end, and an unforeseen rejection is
 *    recorded against the one person it belongs to.
 * 4. **The GraphQL client is resolved lazily.** It is needed only for the
 *    optional profile insert, but it used to be awaited once BEFORE the loop —
 *    so a session hiccup there rejected the mutation and nobody was invited at
 *    all, for a reason unrelated to any of the addresses.
 *
 * The profile is what makes country filtering work later. It is created only when
 * a country is present — otherwise there is nothing to store, and
 * `ensureUserProfile` will create the row (with `userId`) on first sign-in anyway.
 * `userId` is written explicitly because the profile is created by an admin here,
 * which makes `CreatedBy` the admin rather than the person it describes.
 */
export const useInviteUsers = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const inviteUsers = useMutation({
    mutationFn: async ({
      users,
      onProgress,
    }: InviteUsersInput): Promise<InviteUsersResult> => {
      const failures: InviteUsersResult["failures"] = [];
      let createdCount = 0;
      let completed = 0;

      // Resolved on first use, not up front — see point 4 above. A failure clears
      // the cache so a transient one does not poison every later profile insert,
      // while a persistent one still costs at most one attempt per user.
      let clientPromise: ReturnType<typeof gqlClient> | null = null;
      const getClient = async () => {
        clientPromise ??= gqlClient();
        try {
          return await clientPromise;
        } catch (error) {
          clientPromise = null;
          throw error;
        }
      };

      const inviteOne = async (
        user: (typeof users)[number],
      ): Promise<void> => {
        let userId: string;

        try {
          userId = await inviteUser(user);
        } catch (error) {
          // This address alone is out. Return rather than rethrow: the batch and
          // every batch after it carry on untouched.
          failures.push({
            email: user.email,
            reason: inviteFailureReason(error),
          });
          return;
        }

        createdCount += 1;

        // Only worth a row if the sheet actually gave us something to store.
        // With none of these fields, ensureUserProfile creates the profile (with
        // userId) on first sign-in anyway.
        if (!user.country && !user.department && !user.designation) return;

        // A failed profile insert must not lose the account that was just
        // created — report it and move on.
        try {
          const client = await getClient();
          await client.request(INSERT_USER_PROFILE, {
            userId,
            country: user.country ?? null,
            department: user.department ?? null,
            designation: user.designation ?? null,
            wikiSaved: [],
            newsSaved: [],
            wikiDraftIds: [],
            newsDraftIds: [],
          });
        } catch (profileError: any) {
          failures.push({
            email: user.email,
            reason: `account created, but the profile was not saved: ${profileError?.message ?? "unknown error"
              }`,
          });
        }
      };

      const inviteOneTracked = async (
        user: (typeof users)[number],
      ): Promise<void> => {
        onProgress?.({
          completed,
          total: users.length,
          current: {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
          },
        });
        try {
          await inviteOne(user);
        } finally {
          completed += 1;
          onProgress?.({ completed, total: users.length });
        }
      };

      for (let i = 0; i < users.length; i += INVITE_CONCURRENCY) {
        const batch = users.slice(i, i + INVITE_CONCURRENCY);

        const outcomes = await Promise.allSettled(batch.map(inviteOneTracked));

        // `inviteOne` catches everything it can foresee, so a rejection here is
        // something it could not — still attributed to the one person it belongs
        // to rather than being allowed to abort the run.
        outcomes.forEach((outcome, index) => {
          if (outcome.status === "rejected") {
            failures.push({
              email: batch[index].email,
              reason: inviteFailureReason(outcome.reason),
            });
          }
        });
      }

      return { createdCount, failures };
    },
    onSuccess: ({ createdCount, failures }) => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });

      if (failures.length) {
        console.warn("Invite failures:", failures);
        Toast.show({
          // Compared against what was actually created, NOT against
          // `createdCount + failures.length`: a user whose account was created
          // but whose profile insert failed appears in both, so that sum
          // overcounts and could never equal `failures.length`.
          type: createdCount === 0 ? "error" : "info",
          text1: t("INFO_USERS_INVITED_PARTIAL", {
            created: createdCount,
            failed: failures.length,
          }),
          // Which addresses, so the admin can re-run just those instead of
          // hunting them in the console.
          text2: failures.map((failure) => failure.email).join(", "),
        });
        return;
      }

      Toast.show({ type: "success", text1: t("SUCCESS_USERS_INVITED") });
    },
    onError: (error: any) => {
      const serverErrors = error?.response?.data?.errors;
      const emailError = serverErrors?.Email;

      Toast.show({
        type: "error",
        text1: emailError ?? t("ERROR_INVITE_USERS"),
      });
    },
  });

  return { inviteUsers };
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiClient.post(BLOCKS_ENDPOINTS.usersDeactivate, {
        userId,
      });
      await purgeUserData(userId);

      return res;
    },
    onSuccess: () => {
      // Refresh the user list plus every feature surface the purge touched.
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      [
        POSTS_QUERY_KEY,
        COMMENTS_QUERY_KEY,
        NEWS_QUERY_KEY,
        ["leaderboard"],
        [EVENTS_QUERY_KEY],
        [RSVPS_QUERY_KEY],
        [EVENT_GALLERY_QUERY_KEY],
      ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      Toast.show({ type: "success", text1: t("SUCCESS_USER_DELETED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_USER_STATUS_TOGGLED") });
    },
  });
};

// Deactivate = only flip the account inactive. No data purge, no other
export const useDeactivateUserMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post(BLOCKS_ENDPOINTS.usersDeactivate, {
        userId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_USER_DEACTIVATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_USER_STATUS_TOGGLED") });
    },
  });
};

/**
 * Re-enable a deactivated user — `POST /iam/v4/iam/users/activate`
 * `{userId, reason}`. The counterpart to `useDeactivateUserMutation`; verified
 * live 2026-07-30 (`active` false → true, and `isVerified` flips true too).
 *
 * Distinct from `useSendActivationToUser`, which only re-sends the invitation
 * mail and needs the user to click through and set a password. This flips the
 * flag directly and is the only way back for someone an admin deactivated.
 *
 * `reason` is required by the API and is free text; it appears to feed the user's
 * audit timeline, so it says who did it and why rather than being a placeholder.
 */
export const useActivateUserMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post(BLOCKS_ENDPOINTS.usersActivate, {
        userId,
        reason: "AccountActivated",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_USER_ACTIVATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_USER_STATUS_TOGGLED") });
    },
  });
};

export const useSendActivationToUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) =>
      // `POST /iam/v4/auth/resend-activation` — verified to exist (403 for app
      // sessions, not 405/SPA). Body not in the skill docs; the v1 `{userId}`
      // shape is carried over unverified. See `resendActivation` in config.ts.
      apiClient.post(BLOCKS_ENDPOINTS.resendActivation, {
        userId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_USER_DEACTIVATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_USER_STATUS_TOGGLED") });
    },
  });
};

/**
 * `/iam/v4/iam/me` shape → the `BlocksUser` the app was written against.
 *
 * v4 dropped v1's `memberships[]` and returns one `organizationId` plus the
 * roles held there, so the array is synthesized from those two fields.
 * `auth.context` reads `memberships` to decide whether a user belongs to any
 * real org (an empty array sends them to org-select), so this must not be left
 * undefined. `permissions` comes back `[]` for app sessions today.
 */
export const toBlocksUser = (me: BlocksMe): BlocksUser => ({
  itemId: me.itemId,
  firstName: me.firstName ?? "",
  lastName: me.lastName ?? "",
  email: me.email ?? "",
  active: me.active ?? true,
  profileImageUrl: me.profileImageUrl ?? null,
  createdDate: me.createdDate ?? "",
  phoneNumber: me.phoneNumber ?? "",
  logInCount: me.logInCount ?? 0,
  // Kept at the top level as well as inside `memberships`: the memberships array
  // is empty on a `default`-scoped session, which would otherwise discard the
  // user's roles entirely and leave every role badge with nothing to show.
  roles: appRoles(me.roles),
  // `/iam/me` carries the singular `organizationId`; `GET /iam/users/{id}` carries
  // `organizationIds[]` instead. Read both — `auth.context` treats an empty
  // `memberships` as "belongs to no org" and redirects to org-select forever, and
  // `edit-profile` resolves another user's roles through this same array.
  //
  // ⚠ These are MERGED, not preferred one-over-the-other, and `default` is
  // dropped — every consumer of `memberships` wants real orgs.
  //
  // ⚠⚠ This array is NOT a reliable list of the user's memberships, and nothing
  // may treat an empty one as "belongs to no org". `/iam/me` returns a single
  // `organizationId` — the *session's* org — and no `organizationIds` field at
  // all (verified live 2026-08-13: the key is absent, not null). So for the
  // signed-in user this yields at most one entry, and for a session that has not
  // switched org yet that entry is `default`, leaving the array empty.
  // `GET /iam/organizations/my` is the only source that answers "which orgs does
  // this user belong to" — see the `realOrgs` note in auth.context.
  //
  // The plural `organizationIds` only arrives on the `GET /iam/users/{id}` path
  // (another user's record), where it does list every org — the prod user reads
  // `["default", "4681cbb1-…"]` there.
  memberships: Array.from(
    new Set([...(me.organizationId ? [me.organizationId] : []), ...(me.organizationIds ?? [])]),
  )
    .filter(isRealOrgId)
    .map((organizationId) => ({
      organizationId,
      roles: appRoles(me.roles),
      permissions: me.permissions ?? [],
    })),
});

/**
 * v4 replacement for the dead `GET /idp/v1/Iam/GetUser`.
 *
 * Own record → `GET /iam/v4/iam/me` (verified 200). Anyone else →
 * `GET /iam/v4/iam/users/{id}`, also verified 200 since the project granted IAM
 * read rights — NOT `users/gets`, which is not a by-id fetch at all (see
 * `fetchUser`). `retry: false` matters:
 * this query gates the whole auth context's loading spinner, and react-query's
 * default three retries turned the old dead v1 call into an indefinite hang.
 */
const fetchBlocksMe = async (userId: string): Promise<BlocksMe | null> => {
  const me = await fetchMe();
  if (me && me.itemId === userId) return me;
  return fetchUser(userId);
};

const fetchBlocksUser = async (userId: string): Promise<BlocksUser | null> => {
  const raw = await fetchBlocksMe(userId);
  return raw ? toBlocksUser(raw) : null;
};

export const useBlocksUserByIdQuery = (
  userId: string,
  options?: { enabled?: boolean },
) =>
  useQuery<BlocksUser | null>({
    queryKey: BLOCKS_USER_QUERY_KEYS.userById(userId),
    queryFn: () => fetchBlocksUser(userId),
    enabled: (options?.enabled ?? true) && !!userId,
    retry: false,
  });

export type UpdateBlocksUserInput = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  profileImageUrl: string | null;
  // Roles to set for `orgId` only; other orgs' memberships are preserved.
  roles?: string[];
  orgId?: string;
};

export const useUpdateBlocksUserMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      userId,
      input,
    }: {
      userId: string;
      input: UpdateBlocksUserInput;
    }) => {
      // Two different endpoints, because `POST /iam/me` writes ONLY the session's
      // own user: it ignores the body's `itemId`, so using it for somebody else
      // silently overwrote the *editor's* profile. Editing another user goes to
      // `POST /iam/users/{id}` instead (verified live — see `updateUserById`).
      const me = await fetchMe();
      if (!me) throw new Error("no session");

      if (me.itemId !== userId) {
        // Full replace, same as /iam/me — read the target and echo back the
        // fields this edit is not changing, or they are cleared.
        const target = await fetchUser(userId);
        if (!target) {
          throw new Error(`cannot read user ${userId} to update them`);
        }
        await updateUserById(userId, {
          firstName: input.firstName,
          lastName: input.lastName,
          phoneNumber: input.phoneNumber,
          profileImageUrl: input.profileImageUrl,
          salutation: target.salutation ?? "",
          language: target.language ?? "",
          // Keep `clouduser` (platform access) even though the UI never offers it.
          roles: mergePlatformRoles(
            input.roles ?? target.roles ?? [],
            target.roles,
          ),
          permissions: target.permissions ?? [],
        });
        return;
      }

      // Full replace: anything left out is cleared, so unchanged fields are
      // echoed back from the read above.
      await updateMe({
        itemId: me.itemId,
        firstName: input.firstName,
        lastName: input.lastName,
        phoneNumber: input.phoneNumber,
        profileImageUrl: input.profileImageUrl,
        salutation: me.salutation ?? "",
        language: me.language ?? "",
        // v4 has no memberships[] — roles live on the user, scoped to the one
        // org the session is in. Only honour `input.roles` for that org, which
        // is what the old per-membership update did.
        // The UI never shows `clouduser`, so a submitted role list omits it —
        // and this write is a FULL REPLACE. `mergePlatformRoles` adds any
        // platform-only role back from the record, otherwise saving a profile
        // would silently revoke the account's Blocks platform access.
        roles:
          input.roles && input.orgId === me.organizationId
            ? mergePlatformRoles(input.roles, me.roles)
            : (me.roles ?? []),
      });

      // Every post/comment/article/reaction/membership carries a denormalized
      // copy of the author's name (and avatar) taken at write time — see
      // user-name-snapshot-sync's module doc. Only the self-edit branch above
      // can change firstName/lastName (an admin editing someone else's profile
      // is role-only — see `canEditRole` in edit-profile.tsx), so this is the
      // one place a rename can happen. Skip the sync entirely when nothing
      // relevant changed, so a role-only or phone-only save doesn't fire six
      // bulk mutations for no reason. Awaited, but internally per-collection
      // try/catch (like `purgeUserData`) so one failing collection cannot fail
      // this profile save.
      const nameOrAvatarChanged =
        me.firstName !== input.firstName ||
        me.lastName !== input.lastName ||
        me.profileImageUrl !== input.profileImageUrl;

      if (nameOrAvatarChanged) {
        await syncUserNameSnapshots(me.itemId, {
          firstName: input.firstName,
          lastName: input.lastName,
          email: me.email ?? "",
          profileImageUrl: input.profileImageUrl,
        });
      }

      return { itemId: me.itemId, nameOrAvatarChanged };
    },
    onSuccess: (result, { userId, input }) => {
      queryClient.setQueryData(
        BLOCKS_USER_QUERY_KEYS.userById(userId),
        (old: BlocksUser | undefined) => {
          if (!old) return old;
          return {
            ...old,
            firstName: input.firstName,
            lastName: input.lastName,
            phoneNumber: input.phoneNumber,
            profileImageUrl: input.profileImageUrl,
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.userById(userId),
        refetchType: "active",
      });
      // Avatars read their own key (see avatarById) and would otherwise keep
      // showing the old picture for the full staleTime.
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.avatarById(userId),
        refetchType: "active",
      });
      // Refresh the user list so role/name changes show without a manual reload.
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });

      // The mutationFn already rewrote the *stored* authorSnapshot/
      // groupUserSnapshot/reactor copies via syncUserNameSnapshots (see its
      // module doc) — that fixes what a fresh fetch returns. It does nothing
      // for what's already sitting in this client's cache: any posts/comments/
      // articles/reactions/memberships fetched before the rename still hold
      // the old name in memory, which is why it only ever changed after a
      // manual refresh. Invalidate every cache that can embed one of those
      // snapshots so they refetch now, same as `useDeleteUserMutation` already
      // does for the equivalent purge case. Gated on an actual name/avatar
      // change so a role-only or phone-only save doesn't refetch all of this
      // for nothing.
      if (result?.nameOrAvatarChanged) {
        [
          POSTS_QUERY_KEY,
          COMMENTS_QUERY_KEY,
          [REACTIONS_QUERY_KEY],
          NEWS_QUERY_KEY,
          WIKI_ARTICLE_QUERY_KEY,
          WIKI_DRAFT_ARTICLES_QUERY_KEY,
          WIKI_SAVED_ARTICLES_QUERY_KEY,
          WIKI_ARTICLES_BY_CATEGORY_QUERY_KEY,
          ["getGroupMembers"],
          ["getGroupMemberAvatars"],
          // Own-profile "My Posts"/"Saved" tabs: `useGetUserPostsQuery` and
          // `useSavedPostsQuery` key under ["USER_POSTS", userId] and
          // [POSTS_QUERY_KEY, "saved", userId] respectively — neither is a
          // prefix of POSTS_QUERY_KEY (["posts"]) itself, so invalidating that
          // above does not reach them.
          ["USER_POSTS", userId],
          [POSTS_QUERY_KEY, "saved", userId],
          // The mutationFn's syncUserNameSnapshots call already rewrote the
          // *stored* leaderboard userSnapshot server-side (see its module
          // doc) — this just makes this client's cache pick it up.
          ["leaderboard"],
        ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      }

      Toast.show({ type: "success", text1: t("SUCCESS_PROFILE_UPDATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_PROFILE_UPDATE_FAILED") });
    },
  });
};

export type UserRole = "admin" | "member";
export type UserStatus = "active" | "inactive";
export type StatItem = {
  count: number;
  text: string;
  icon: React.ReactElement;
};

export const useUsersByIdsQuery = (userIds: string[]) =>
  useQuery<UsersResponse>({
    queryKey: BLOCKS_USER_QUERY_KEYS.usersByIds(userIds),
    enabled: userIds.length > 0,
    queryFn: async () => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      const response = await apiClient.post(BLOCKS_ENDPOINTS.users, {
        page: 0,
        pageSize: userIds.length,
        sort: { property: "CreatedDate", isDescending: false },
        filter: {
          name: "",
          email: "",
          userIds,
          ...(currentOrgId && { org_id: currentOrgId }),
        },
      });

      return {
        users: toBlocksUserList(response.data.data),
        totalCount: response.data.totalCount,
        pageSize: userIds.length,
      };
    },
  });
